import React, {useState} from 'react';
import {Button, ButtonToolbar, Form, Message, Radio, RadioGroup, SelectPicker, useToaster} from "rsuite";
import {useFormik} from "formik";
import {rsuiteInputOnchangeFormikHandler, rsuiteSelectOnchangeFormikHandler} from "util/Helper";
import Modal from "rsuite/Modal";
import * as Yup from "yup";
import LocationsTimezones from "util/LocationsTimezones";

const validationSchema = Yup.object().shape({
    org: Yup.string()
        .min(2, 'Too Short!')
        .max(250, 'Too Long!')
        .matches(/^\S*$/, 'Location name should not contain spaces!')
        .matches(/^[ A-Za-z0-9_-]*$/, 'Special chars are not allowed')
        .required('Required'),
    timezone: Yup.string().nullable().required('Required'),
    type: Yup.string().required('Required'),
});
const selectData = LocationsTimezones.map(({name, offset}) => {
    return {
        label: `${name} |  ${offset}`,
        value: name
    }
})

const AddEditLocation = (props) => {
    const {
        formValues,
        sendRequest,
        modalOpen,
        setModalOpen,
        getFacilities,
        modalTitle,
        disabledFields,
        requestSuccessMessage,
        requestErrorMessage,
        shouldResetForm
    } = props;

    const toastMessage = useToaster()
    const [formDisabled, setFormDisabled] = useState(false)


    const formik = useFormik({
        initialValues: {...formValues},
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            await setFormDisabled(true);

            try {
                await sendRequest({...values})

                toastMessage.push(
                    <Message type="success">
                        <h6>Success</h6>
                        <p className='m-0'>{requestSuccessMessage}</p>
                    </Message>,
                    {
                        placement: "topEnd"
                    });

                getFacilities ? await getFacilities() : null;

                await closeModal();

            } catch (error) {
                toastMessage.push(
                    <Message type="error">
                        <h6>Error</h6>
                        <p className='m-0'>{requestErrorMessage}</p>
                    </Message>,
                    {
                        placement: "topEnd"
                    });
            }

            await setFormDisabled(false);
        },
    });
    const formikOnInputChange = rsuiteInputOnchangeFormikHandler(formik.handleChange);
    const formikOnSelectChange = rsuiteSelectOnchangeFormikHandler(formik.handleChange, 'timezone');

    const closeModal = () => {
        shouldResetForm ? formik.resetForm() : null;
        setModalOpen(false)
    }

    return (
        <Modal open={modalOpen} onClose={closeModal} className="locations-modal add-location-modal">
            <Modal.Header>
                <Modal.Title><strong>{modalTitle}</strong></Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form fluid onSubmit={formik.handleSubmit} disabled={formDisabled}>
                    <Form.Group controlId="org">
                        <Form.ControlLabel  className='add-location-label add-location-label-required'>Name</Form.ControlLabel>
                        <Form.Control
                            disabled={disabledFields.includes('org')}
                            className={'add-location-input'}
                            type='text'
                            name="org"
                            placeholder='Location name'
                            onChange={formikOnInputChange}
                            value={formik.values.org }
                            errorMessage={formik.touched.org && formik.errors.org ? formik.errors.org : null}
                        />
                    </Form.Group>

                    <Form.Group controlId="timezone">
                        <Form.ControlLabel className='add-location-label-required'>Time Zone</Form.ControlLabel>
                        <Form.Control
                            disabled={disabledFields.includes('timezone')}
                            className={'add-location-input'}
                            accepter={SelectPicker}
                            name='timezone'
                            block
                            data={selectData}
                            onChange={formikOnSelectChange}
                            value={formik.values.timezone}
                            errorMessage={formik.touched.timezone && formik.errors.timezone ? formik.errors.timezone : null}
                        />
                    </Form.Group>

                    <Form.Group controlId="type">
                        <Form.ControlLabel  className='add-location-label-required'>Type</Form.ControlLabel>
                        <Form.Control
                            disabled={disabledFields.includes('type')}
                            accepter={RadioGroup}
                            name='type'
                            inline
                            value={formik.values.type}
                            onChange={formikOnInputChange}
                            className={'add-location-input'}
                            errorMessage={formik.touched.type && formik.errors.type ? formik.errors.type : null}
                        >
                            <Radio value="false">Facility</Radio>
                            <Radio value="true">Home Health</Radio>
                        </Form.Control>
                    </Form.Group>

                    <Form.Group controlId="bth-toolbar">
                        <ButtonToolbar className='btn-toolbar'>
                            <Button
                                type='reset'
                                appearance="ghost"
                                disabled={formDisabled}
                                size='lg'
                                className='add-location-btn btn-cancel'
                                onClick={closeModal}
                            >Cancel</Button>
                            <Button
                                type='submit'
                                appearance="primary"
                                loading={formDisabled}
                                size='lg'
                                className='add-location-btn btn-save'
                            >Save</Button>
                        </ButtonToolbar>
                    </Form.Group>

                </Form>
            </Modal.Body>
        </Modal>
    );
};

AddEditLocation.defaultProps = {
    formValues: {
        org: '',
        timezone: '',
        type: '',
    },
    disabledFields: [],
    modalTitle: 'New Location',
    getFacilities: null,
    requestSuccessMessage: 'Locations updated',
    requestErrorMessage: 'Something went wrong while updating locations, try again later',
    modalOpen: false,
    shouldResetForm: true
}

export default AddEditLocation;